
type Task = () => Promise<void>
type CompletionResolver = () => void

export class ConcurrentTaskRunner {
	private maxConcurrentTasks: number
	private lastTaskId = 0
	private running: number[] = []
	private pending: Task[] = []
	private completionListeners: CompletionResolver[] = []
	name = 'ConcurrentTaskRunner'
	debug = false

	constructor(maxConcurrentTasks: number) {
		this.maxConcurrentTasks = maxConcurrentTasks
	}

	isActive() {
		return (this.running.length > 0 || this.pending.length > 0)
	}

	push(pushed: Task): Promise<void> {
		const taskId = ++this.lastTaskId
		if(this.debug) console.debug(`[${this.name}] 👌 received #${taskId}`)
		// return a promise to the pusher
		// that resolves once the pushed task has completed
		return new Promise<void>((resolve, reject) => {
			const pushedRunner = async () => {
				this.running.push(taskId)
				if(this.debug) console.debug(`[${this.name}] 🏃 running #${taskId}`)
				try {
					await pushed()
					resolve()
				} catch(ex) {
					reject(ex)
				} finally {
					this.running.splice(this.running.findIndex(id => id === taskId), 1)
					if(this.debug) console.debug(`[${this.name}] 😎 completed #${taskId}, ${this.running.length} / ${this.maxConcurrentTasks} running, ${this.pending.length} pending`)
					// when this task is done,
					if(!this.isActive()) {
						// if there are no other running or pending tasks,
						// notify finish waiters
						const completionListeners = this.completionListeners
						this.completionListeners = []
						completionListeners.forEach(notify => notify())
					} else if(this.running.length < this.maxConcurrentTasks) {
						// or, if there is an available concurrency slot
						// and a pending task is available,
						// execute it now
						const pendingRunner = this.pending.shift()
						if(pendingRunner) {
							pendingRunner()
						}
					}
				}
			}
			if (this.running.length < this.maxConcurrentTasks) {
				// slot available, run this now
				pushedRunner()
			} else {
				// slot not available, it's pending
				if(this.debug) console.debug(`[${this.name}] 🛑 queueing #${taskId}`)
				this.pending.push(pushedRunner)
			}
		})
	}

	async finished(): Promise<void> {
		// if there are no running or pending tasks,
		// notify immediately
		if(!this.isActive()) {
			return Promise.resolve()
		}
		// otherwise, queue up for later notification
		return new Promise<void>((resolve, reject) => {
			this.completionListeners.push(resolve)
		})
	}
}
